/**
 * The care team's order, wherever it is listed (the People tab's Care
 * providers card, the face sheet, the prescriber box's care-team group).
 *
 * The active rows fall in three groups, in this order:
 *   - notInDirectory: a typed name (unlinked) or one waiting on the
 *     steward (pending), first, so it gets linked;
 *   - people: rows linked to a Directory individual;
 *   - organizations: rows linked to a Directory organization.
 * Each group is alphabetical by the name as shown, ignoring case and
 * accents, with numbers in number order. A leading title (Dr., Mr., Mrs.,
 * Ms. and the like) is skipped for the sort only; the name shown is
 * unchanged, and no surname is guessed ("Dr. Zed Adams" sorts as "Zed
 * Adams"). Ties go by row id, ascending. Archived rows are left out.
 */

import type { ProviderRow } from './clientRecord';

export interface CareProviderGroups {
  notInDirectory: ProviderRow[];
  people: ProviderRow[];
  organizations: ProviderRow[];
}

/** A leading title and its optional full stop, then the space after it. */
const LEADING_TITLE = /^(?:dr|mr|mrs|ms|mx|miss|prof|rev)\.?\s+/i;

/** The name a row sorts by: as shown, trimmed, without a leading title. */
export function careProviderSortName(displayName: string): string {
  const name = displayName.trim();
  const bare = name.replace(LEADING_TITLE, '');
  return bare === '' ? name : bare;
}

function byName(a: ProviderRow, b: ProviderRow): number {
  const cmp = careProviderSortName(a.displayName).localeCompare(careProviderSortName(b.displayName), undefined, {
    sensitivity: 'base',
    numeric: true,
  });
  if (cmp !== 0) return cmp;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function groupCareProviders(rows: readonly ProviderRow[]): CareProviderGroups {
  const groups: CareProviderGroups = { notInDirectory: [], people: [], organizations: [] };
  for (const row of rows) {
    if (!row.isActive) continue;
    if (row.link.kind === 'individual') groups.people.push(row);
    else if (row.link.kind === 'org') groups.organizations.push(row);
    else groups.notInDirectory.push(row);
  }
  groups.notInDirectory.sort(byName);
  groups.people.sort(byName);
  groups.organizations.sort(byName);
  return groups;
}

/** The active rows as one list, in the groups' order. */
export function orderedCareProviders(rows: readonly ProviderRow[]): ProviderRow[] {
  const g = groupCareProviders(rows);
  return [...g.notInDirectory, ...g.people, ...g.organizations];
}
